import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/auth';
import { usePreferences } from '@/auth/hooks/usePreferences';
import { checkAllPermissions, PERMISSIONS } from '@/auth/permissions';
import { TRAMITE_LABELS } from '@/common/constants';
import { FEATURES } from '@/config/features';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  permission?: keyof typeof PERMISSIONS;
}

/** `permission` es la clave de PERMISSIONS; el usuario trae los nombres reales (p. ej. `tramites.manage`). */
export const hasColumnPermission = (userPermissions: string[], column: ColumnConfig) => {
  if (!column.permission) return true;
  return checkAllPermissions(userPermissions, [PERMISSIONS[column.permission].name]);
};

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'id', label: '# Trámite', visible: true },
  { key: 'numregistro', label: '# Registro', visible: true },
  { key: 'vendedor', label: TRAMITE_LABELS.TRANSFERENTE, visible: true },
  { key: 'comprador', label: TRAMITE_LABELS.ADQUIRIENTE, visible: true },
  { key: 'asignados', label: 'Asignados', visible: true },
  { key: 'fecini', label: 'Fecha de inicio', visible: true, permission: 'MANAGE_TRAMITES' },
  { key: 'fecfirma', label: 'Fecha de firma', visible: true, permission: 'MANAGE_TRAMITES' },
  { key: 'fecescritura', label: 'Fecha de escritura', visible: true },
  { key: 'num_escritura', label: '# Escritura', visible: true },
  { key: 'vencimientoPresentacion', label: 'Vencimiento presentación', visible: true, permission: 'MANAGE_TRAMITES' },
  { key: 'tramites', label: 'Trámites', visible: true },
  { key: 'tareas', label: 'Tareas', visible: true, permission: 'VIEW_TRAMITE_TAREAS' },
  { key: 'bloqueada', label: 'Bloqueada', visible: true, permission: 'MANAGE_BLOQUEO_TRAMITE' },
  ...(FEATURES.archivadoEscrituras
    ? [{ key: 'archivada', label: 'Archivada', visible: true, permission: 'MANAGE_ARCHIVADO_TRAMITE' } satisfies ColumnConfig]
    : []),
  { key: 'indicadores', label: 'Indicadores', visible: true },
  { key: 'acciones', label: 'Acciones', visible: true },
];

interface ColumnVisibilityContextType {
  columns: ColumnConfig[];
  toggleColumn: (columnKey: string) => void;
  resetColumns: () => void;
  getVisibleColumns: () => ColumnConfig[];
  isColumnVisible: (columnKey: string) => boolean;
}

const ColumnVisibilityContext = createContext<ColumnVisibilityContextType | undefined>(undefined);

export const ColumnVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userPermissions = user?.permissions || [];
  const { preferences, updatePreferences, isLoading } = usePreferences();
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const hasLoadedFromPreferences = React.useRef(false);

  const loadFromPreferences = useCallback(() => {
    try {
      const visibilityMap = preferences?.tramites?.columnVisibility;
      if (visibilityMap) {
        return DEFAULT_COLUMNS.map(col => ({
          ...col,
          visible: visibilityMap[col.key] !== undefined ? visibilityMap[col.key] : col.visible
        }));
      }
    } catch (error) {
      console.warn('Error loading column visibility from preferences:', error);
    }
    return DEFAULT_COLUMNS;
  }, [preferences]);

  const toggleColumn = useCallback((columnKey: string) => {
    setColumns(prev => {
      const updated = prev.map(col =>
        col.key === columnKey ? { ...col, visible: !col.visible } : col
      );

      // Save to DB immediately (debounced by usePreferences)
      const visibilityMap = updated.reduce((acc, col) => {
        acc[col.key] = col.visible;
        return acc;
      }, {} as Record<string, boolean>);

      updatePreferences({
        ...preferences,
        tramites: {
          ...preferences.tramites,
          columnVisibility: visibilityMap,
        },
      });

      return updated;
    });
  }, [preferences, updatePreferences]);

  const resetColumns = useCallback(() => {
    const visibilityMap = DEFAULT_COLUMNS.reduce((acc, col) => {
      acc[col.key] = col.visible;
      return acc;
    }, {} as Record<string, boolean>);

    updatePreferences({
      ...preferences,
      tramites: {
        ...preferences.tramites,
        columnVisibility: visibilityMap,
      },
    });

    setColumns([...DEFAULT_COLUMNS]);
  }, [preferences, updatePreferences]);

  const getVisibleColumns = useCallback(() => {
    return columns.filter(col => col.visible && hasColumnPermission(userPermissions, col));
  }, [columns, userPermissions]);

  const isColumnVisible = useCallback((columnKey: string) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || !column.visible) return false;
    return hasColumnPermission(userPermissions, column);
  }, [columns, userPermissions]);

  useEffect(() => {
    if (!isLoading && preferences?.tramites?.columnVisibility) {
      hasLoadedFromPreferences.current = true;
      const loadedColumns = loadFromPreferences();
      setColumns(loadedColumns);
    }
  }, [isLoading, preferences, loadFromPreferences]);

  const contextValue: ColumnVisibilityContextType = {
    columns,
    toggleColumn,
    resetColumns,
    getVisibleColumns,
    isColumnVisible,
  };

  return (
    <ColumnVisibilityContext.Provider value={contextValue}>
      {children}
    </ColumnVisibilityContext.Provider>
  );
};

export const useColumnVisibility = () => {
  const context = useContext(ColumnVisibilityContext);
  if (context === undefined) {
    throw new Error('useColumnVisibility must be used within a ColumnVisibilityProvider');
  }
  return context;
};
